import { useEffect, useState } from 'react'

const INTERACTION_EVENTS: (keyof WindowEventMap)[] = [
  'scroll',
  'pointerdown',
  'keydown',
]

/**
 * 計測系スクリプトなど、初期表示に不要なものの読み込みを遅らせるためのフック。
 *
 * load イベント後のアイドル時、またはユーザーの最初の操作の、どちらか早い方で true を返す。
 * next/script の lazyOnload と同じ挙動を、strategy を受け取らないコンポーネント
 * （@next/third-parties の GoogleTagManager など）でも使えるようにしたもの。
 *
 * requestIdleCallback の timeout は「最大待ち時間」であって最小遅延ではないため、
 * load を待たずに要求すると初期描画中の僅かな空きで即座に発火してしまう
 * （実測で約 500ms、ほとんど遅延できていなかった）。load 後にアイドルを要求することで、
 * 初期表示とのリソース競合を確実に避ける。
 *
 * @param timeout load 後、アイドルを待つ上限 (ms)。アイドルが来なくてもこの時間で発火する
 */
export default function useDeferredMount(timeout = 3000) {
  const [ isReady, setReady ] = useState(false)

  useEffect(() => {
    if (isReady) {
      return
    }

    const hasIdleCallback = typeof window.requestIdleCallback === 'function'
    let idleId: number | undefined

    const activate = () => setReady(true)

    const scheduleIdle = () => {
      idleId = hasIdleCallback
        ? window.requestIdleCallback(activate, { timeout })
        : window.setTimeout(activate, 0)
    }

    INTERACTION_EVENTS.forEach((event) => {
      window.addEventListener(event, activate, { once: true, passive: true })
    })

    if (document.readyState === 'complete') {
      scheduleIdle()
    }
    else {
      window.addEventListener('load', scheduleIdle, { once: true })
    }

    return () => {
      INTERACTION_EVENTS.forEach((event) => {
        window.removeEventListener(event, activate)
      })
      window.removeEventListener('load', scheduleIdle)

      if (idleId === undefined) {
        return
      }
      if (hasIdleCallback) {
        window.cancelIdleCallback(idleId)
      }
      else {
        window.clearTimeout(idleId)
      }
    }
  }, [ isReady, timeout ])

  return isReady
}
